/**
 * Funzioni e hook per la gestione dei sotto-processi attività
 * @module Pages/Admin/SottoProcessi/Functions
 */

import { useEffect, useState } from 'react'
import { FC, invalidateQuery, useCQuery } from '../../../Services'

/**
 * Hook per gestire lo stato di un sotto-processo
 * @param {string} sottoProcessoId - ID del sotto-processo da caricare
 * @returns {Array} [sottoProcesso, setSottoProcessoProp, isSuccess]
 */
export const useSottoProcesso = (sottoProcessoId) => {
  const [sottoProcesso, setSottoProcesso] = useState()
  const { data, isSuccess } = useCQuery(['sottoProcessiAttivita', sottoProcessoId])

  useEffect(() => {
    if (isSuccess && data) {
      setSottoProcesso(data)
    }
  }, [isSuccess, data])

  const setSottoProcessoProp = (prop, value) =>
    setSottoProcesso({ ...sottoProcesso, [prop]: value })

  return [sottoProcesso, setSottoProcessoProp, isSuccess || sottoProcessoId === 'new']
}

/**
 * Salva un sotto-processo (crea nuovo o aggiorna esistente)
 * @param {Object} sottoProcesso - Oggetto sotto-processo da salvare
 * @returns {Promise<Object|boolean>} Risultato del salvataggio o false in caso di errore
 */
export const saveSottoProcesso = async (sottoProcesso) => {
  if (!sottoProcesso?.codice) {
    window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Inserire il codice' })
    return false
  }
  if (sottoProcesso.id === 'new') {
    delete sottoProcesso.id
    try {
      const res = await FC.service('sottoProcessiAttivita').create({ ...sottoProcesso })
      invalidateQuery(['sottoProcessiAttivita'])
      window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Sotto-processo creato' })
      return res
    } catch (e) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore durante il salvataggio' })
      return false
    }
  }
  try {
    const res = await FC.service('sottoProcessiAttivita').patch(sottoProcesso.id, sottoProcesso)
    invalidateQuery(['sottoProcessiAttivita'])
    window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Sotto-processo aggiornato' })
    return res
  } catch (e) {
    window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore durante il salvataggio' })
    return false
  }
}

/**
 * Elimina (soft delete) un sotto-processo
 * @param {number} sottoProcessoId - ID del sotto-processo da eliminare
 * @returns {Promise<Object>} Risultato dell'eliminazione
 */
export const deleteSottoProcesso = async (sottoProcessoId) => {
  const res = await FC.service('sottoProcessiAttivita').patch(sottoProcessoId, { deleted: true })
  invalidateQuery(['sottoProcessiAttivita'])
  window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Sotto-processo disabilitato' })
  return res
}

/**
 * Riabilita un sotto-processo precedentemente eliminato
 * @param {number} sottoProcessoId - ID del sotto-processo da riabilitare
 * @returns {Promise<Object>} Risultato della riabilitazione
 */
export const restoreSottoProcesso = async (sottoProcessoId) => {
  const res = await FC.service('sottoProcessiAttivita').patch(sottoProcessoId, { deleted: false })
  invalidateQuery(['sottoProcessiAttivita'])
  window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Sotto-processo riabilitato' })
  return res
}

